import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal, ViewChild } from '@angular/core';
import { API, TemplateDataSetDTO, TemplateGroupDTO } from '../../../services/api/api';
import { SbSplitButton } from '../../../services/global/global-interfaces';
import { UtilService } from '../../../services/util.service';
import { ErrorService } from '../../../services/error.service';
import { PermissionService } from '../../../services/permission.service';
import { SbSplitButtonItem, SearchOptions } from '@ngx-seatback/controls';
import { FilterPanelComponent, FilterPanelConfig } from '@ngx-seatback/layout';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from '../../../services/transformation-helper/string-transformation.service';
import { RequestClass } from '../../../services/global/RequestClass';
import { RoutingID } from '../../../routes/sb-routing-enum';

type SplitButtonAction = 'edit' | 'show';

@Component({
  selector: 'sb-page-template-dataset',
  templateUrl: './template-dataset.component.html',
  styleUrls: ['./template-dataset.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TemplateDatasetComponent extends RequestClass {
  public readonly util = inject(UtilService);
  public readonly errorHandler = inject(ErrorService);
  public readonly permissionService = inject(PermissionService);
  protected readonly api = inject(API);
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  @ViewChild('FilterPanelRef') filterPanelRef: FilterPanelComponent;

  // table wrapper variables
  templateDatasets: TemplateDataSetDTO[] = [];

  // filter variables
  filteredTemplateGroups: TemplateGroupDTO[] = [];
  templateGroups: TemplateGroupDTO[] = [];
  exactMatch: SearchOptions = 'contains';
  search: string | null = null;
  filterConfig: FilterPanelConfig = {
    showExactMatch: true,
    showSearchHeadline: true,
    button: { visibleWithoutChanges: true },
  };

  refreshFromServer() {
    if (!this.pagination) {
      this.loading.set(false);
      return;
    }
    const templateGroupIds = this.filteredTemplateGroups?.length > 0 ? this.filteredTemplateGroups.map(group => group.id) : null;

    const sub = this.api
      .mm_templating_advertiser_TemplateDataSet_retrieveAll(
        {
          group_ids: templateGroupIds?.length > 0 ? templateGroupIds : null,
          search: this.stringTransformation.encodeSearchQueryParamWithOptions(this.exactMatch, this.search),
        },
        this.pagination
      )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loading.set(false);
        })
      )
      .subscribe((templateDatasets: TemplateDataSetDTO[]) => {
        this.templateDatasets = this.buildSplitButtons(templateDatasets);
      });
    this.subs.push(sub);
  }

  createTemplateDataset() {
    this.util.navService.navigateToPoint(this.util.RoutingID.templateDatasetCreate, null, {
      queryParams: { returnTo: this.util.getPageLink() },
    });
  }

  buildSplitButtons<T extends { id: any }>(itemsToAdd: T[]): (T & SbSplitButton<SplitButtonAction>)[] {
    const result = itemsToAdd.map(item => {
      return { ...item, sbButtons: [] as SbSplitButtonItem<SplitButtonAction>[] };
    });
    result.forEach(item => {
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_data_set_create])) {
        item.sbButtons.push({
          icon: 'edit',
          tooltip: 'lang.globals.edit',
          link: RoutingID.templateDatasetEdit,
          pathParams: [{ id: 'templateDatasetID', value: item?.id }],
        });
      }
      if (this.permissionService.hasPermissions([this.permissionService.Permission.template_data_set_entry_list])) {
        item.sbButtons.push({
          icon: 'templateEntry',
          tooltip: 'lang.globals.showEntries',
          link: RoutingID.templateDatasetEntries,
          pathParams: [{ id: 'templateDatasetID', value: item?.id }],
        });
      }
    });
    return result;
  }

  applyFiltering(filterMap: Map<string, any[]>) {
    this.setRequestOptions(filterMap);
    this.requestDebounced();
  }

  setRequestOptions(filterMap: Map<string, any[]>) {
    this.search = filterMap.get('search')?.[0];
    this.exactMatch = filterMap.get('exactMatch')?.[0];
    this.filteredTemplateGroups = filterMap.get('templateGroups');
  }
}
